import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Alert, Button } from '@/components/ui/daisy';

import {
  AlertToastActionProps,
  AlertToastContentProps,
  AlertToastIconProps,
  AlertToastMessageProps,
  AlertToastProps,
  ToastColor,
} from './types';

const iconMap: Record<ToastColor, JSX.Element> = {
  success: <CheckCircleIcon className="w-6 h-6 shrink-0" />,
  error: <XCircleIcon className="w-6 h-6 shrink-0" />,
  warning: <ExclamationTriangleIcon className="w-6 h-6 shrink-0" />,
  info: <InformationCircleIcon className="w-6 h-6 shrink-0" />,
};

const AlertToastBase = forwardRef<HTMLDivElement, AlertToastProps>(
  ({ className, variant = 'default', children, ...props }, ref) => (
    <Alert
      ref={ref}
      variant={variant}
      className={twMerge('max-w-xl', className)}
      {...props}
    >
      {children}
    </Alert>
  ),
);

const Icon = ({ color, children }: AlertToastIconProps) => (
  <>
    {children ??
      (color ? (
        iconMap[color]
      ) : (
        <InformationCircleIcon className="w-6 h-6 shrink-0 text-info" />
      ))}
  </>
);

const Message = forwardRef<HTMLSpanElement, AlertToastMessageProps>(
  ({ className, children, ...props }, ref) => (
    <span ref={ref} className={twMerge('text-base', className)} {...props}>
      {children}
    </span>
  ),
);

const Content = ({ title, description }: AlertToastContentProps) => (
  <div className="flex flex-col">
    <span className="text-sm font-bold">{title}</span>
    <ConditionalRenderer condition={!!description}>
      <span className="text-xs">{description}</span>
    </ConditionalRenderer>
  </div>
);

const Action = ({ label, onClick }: AlertToastActionProps) => (
  <Button color="primary" size="sm" onClick={onClick}>
    {label}
  </Button>
);

AlertToastBase.displayName = 'AlertToast';
Icon.displayName = 'AlertToast.Icon';
Message.displayName = 'AlertToast.Message';
Content.displayName = 'AlertToast.Content';
Action.displayName = 'AlertToast.Action';

export const AlertToast = Object.assign(AlertToastBase, {
  Icon,
  Message,
  Content,
  Action,
});
